import { z } from 'zod';

import { NonEmptyStringSchema } from './primitives.js';

export const TimePrecisionSchema = z.enum(['day', 'month', 'year', 'decade']);

export type TimePrecision = z.infer<typeof TimePrecisionSchema>;

type TimeParts = readonly [year: number, month: number, day: number];

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const YEAR_PATTERN = '-?(?:0|[1-9]\\d*)';
const YEAR_RE = new RegExp(`^(${YEAR_PATTERN})$`, 'u');
const MONTH_RE = new RegExp(`^(${YEAR_PATTERN})-(\\d{2})$`, 'u');
const DAY_RE = new RegExp(`^(${YEAR_PATTERN})-(\\d{2})-(\\d{2})$`, 'u');
const DECADE_RE = new RegExp(`^(${YEAR_PATTERN})s$`, 'u');

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year: number, month: number): number {
  const lengths = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return lengths[month - 1] ?? 0;
}

function parseTimeParts(iso: string, precision: TimePrecision): TimeParts | undefined {
  const match =
    precision === 'day'
      ? DAY_RE.exec(iso)
      : precision === 'month'
        ? MONTH_RE.exec(iso)
        : precision === 'year'
          ? YEAR_RE.exec(iso)
          : DECADE_RE.exec(iso);
  if (match?.[1] === undefined) {
    return undefined;
  }
  const year = Number(match[1]);
  if (!Number.isSafeInteger(year)) {
    return undefined;
  }
  if (precision === 'decade' && year % 10 !== 0) {
    return undefined;
  }
  const month = match[2] === undefined ? 1 : Number(match[2]);
  const day = match[3] === undefined ? 1 : Number(match[3]);
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    return undefined;
  }
  if (!Number.isInteger(day) || day < 1 || day > daysInMonth(year, month)) {
    return undefined;
  }
  return [year, month, day];
}

function compareParts(left: TimeParts, right: TimeParts): number {
  return left[0] - right[0] || left[1] - right[1] || left[2] - right[2];
}

function maximumParts(parts: TimeParts, precision: TimePrecision): TimeParts {
  if (precision === 'day') {
    return parts;
  }
  if (precision === 'month') {
    return [parts[0], parts[1], daysInMonth(parts[0], parts[1])];
  }
  if (precision === 'year') {
    return [parts[0], 12, 31];
  }
  return [parts[0] + 9, 12, 31];
}

const QualifiedTimeBaseSchema = z.object({
  iso: NonEmptyStringSchema,
  precision: TimePrecisionSchema,
  circa: z.boolean().optional(),
  earliest: NonEmptyStringSchema.optional(),
  latest: NonEmptyStringSchema.optional(),
});

export const QualifiedTimeSchema = QualifiedTimeBaseSchema.superRefine((value, context) => {
  const nominal = parseTimeParts(value.iso, value.precision);
  if (nominal === undefined) {
    context.addIssue({
      code: 'custom',
      path: ['iso'],
      message: `Time value does not match ${value.precision} precision.`,
    });
    return;
  }
  const earliest =
    value.earliest === undefined ? undefined : parseTimeParts(value.earliest, value.precision);
  const latest =
    value.latest === undefined ? undefined : parseTimeParts(value.latest, value.precision);
  if (value.earliest !== undefined && earliest === undefined) {
    context.addIssue({
      code: 'custom',
      path: ['earliest'],
      message: `Earliest value does not match ${value.precision} precision.`,
    });
  }
  if (value.latest !== undefined && latest === undefined) {
    context.addIssue({
      code: 'custom',
      path: ['latest'],
      message: `Latest value does not match ${value.precision} precision.`,
    });
  }
  if (earliest !== undefined && compareParts(earliest, nominal) > 0) {
    context.addIssue({
      code: 'custom',
      path: ['earliest'],
      message: 'Earliest value cannot be later than the nominal time.',
    });
  }
  if (
    latest !== undefined &&
    compareParts(maximumParts(latest, value.precision), maximumParts(nominal, value.precision)) < 0
  ) {
    context.addIssue({
      code: 'custom',
      path: ['latest'],
      message: 'Latest value cannot be earlier than the nominal time.',
    });
  }
});

export type QualifiedTime = z.infer<typeof QualifiedTimeSchema>;

type TimeBounds = {
  minimum: TimeParts;
  maximum: TimeParts;
};

export type QualifiedTimeYearBounds = {
  minimum: number;
  maximum: number;
};

function qualifiedTimeBounds(time: QualifiedTime): TimeBounds {
  const nominal = parseTimeParts(time.iso, time.precision);
  const earliest =
    time.earliest === undefined ? undefined : parseTimeParts(time.earliest, time.precision);
  const latest =
    time.latest === undefined ? undefined : parseTimeParts(time.latest, time.precision);
  if (nominal === undefined || (time.earliest !== undefined && earliest === undefined)) {
    throw new Error(`Invalid ${time.precision}-precision time: ${time.iso}.`);
  }
  if (time.latest !== undefined && latest === undefined) {
    throw new Error(`Invalid latest ${time.precision}-precision time: ${time.latest}.`);
  }
  return {
    minimum: earliest ?? nominal,
    maximum: maximumParts(latest ?? nominal, time.precision),
  };
}

export function compareQualifiedTimes(left: QualifiedTime, right: QualifiedTime): number {
  return compareParts(qualifiedTimeBounds(left).minimum, qualifiedTimeBounds(right).minimum);
}

export function qualifiedTimeYearBounds(time: QualifiedTime): QualifiedTimeYearBounds {
  const bounds = qualifiedTimeBounds(time);
  return { minimum: bounds.minimum[0], maximum: bounds.maximum[0] };
}

export function compareRelationshipStartsDescending(
  left: { start?: QualifiedTime | undefined },
  right: { start?: QualifiedTime | undefined },
): number {
  if (left.start === undefined && right.start === undefined) return 0;
  if (left.start === undefined) return 1;
  if (right.start === undefined) return -1;
  return compareQualifiedTimes(right.start, left.start);
}

const PRECISION_RANK: Readonly<Record<TimePrecision, number>> = {
  decade: 0,
  year: 1,
  month: 2,
  day: 3,
};

function earlierIso(left: string | undefined, right: string | undefined, precision: TimePrecision) {
  if (left === undefined) return right;
  if (right === undefined) return left;
  const leftParts = parseTimeParts(left, precision);
  const rightParts = parseTimeParts(right, precision);
  if (leftParts === undefined || rightParts === undefined) {
    throw new Error('Cannot reconcile invalid temporal bounds.');
  }
  return compareParts(leftParts, rightParts) <= 0 ? left : right;
}

function laterIso(left: string | undefined, right: string | undefined, precision: TimePrecision) {
  if (left === undefined) return right;
  if (right === undefined) return left;
  const leftParts = parseTimeParts(left, precision);
  const rightParts = parseTimeParts(right, precision);
  if (leftParts === undefined || rightParts === undefined) {
    throw new Error('Cannot reconcile invalid temporal bounds.');
  }
  return compareParts(leftParts, rightParts) >= 0 ? left : right;
}

export function reconcileQualifiedTime(
  left: QualifiedTime | undefined,
  right: QualifiedTime | undefined,
): QualifiedTime | undefined {
  if (left === undefined) return right;
  if (right === undefined) return left;
  const leftBounds = qualifiedTimeBounds(left);
  const rightBounds = qualifiedTimeBounds(right);
  const overlaps =
    compareParts(leftBounds.minimum, rightBounds.maximum) <= 0 &&
    compareParts(rightBounds.minimum, leftBounds.maximum) <= 0;
  if (!overlaps) {
    return undefined;
  }
  if (left.precision !== right.precision) {
    const preferred =
      PRECISION_RANK[left.precision] > PRECISION_RANK[right.precision] ? left : right;
    return QualifiedTimeSchema.parse({
      ...preferred,
      ...(left.circa === true || right.circa === true ? { circa: true } : {}),
    });
  }
  if (left.iso !== right.iso) {
    return undefined;
  }
  const earliest = earlierIso(left.earliest, right.earliest, left.precision);
  const latest = laterIso(left.latest, right.latest, left.precision);
  return QualifiedTimeSchema.parse({
    iso: left.iso,
    precision: left.precision,
    ...(left.circa === true || right.circa === true ? { circa: true } : {}),
    ...(earliest === undefined ? {} : { earliest }),
    ...(latest === undefined ? {} : { latest }),
  });
}

export function relationshipTimeOrderIsValid(
  start: QualifiedTime | undefined,
  end: QualifiedTime | undefined,
): boolean {
  if (start === undefined || end === undefined) {
    return true;
  }
  return compareParts(qualifiedTimeBounds(start).minimum, qualifiedTimeBounds(end).maximum) <= 0;
}

export function assertRelationshipTimeOrder(
  start: QualifiedTime | undefined,
  end: QualifiedTime | undefined,
): void {
  if (!relationshipTimeOrderIsValid(start, end)) {
    throw new Error(`Relationship start ${start?.iso ?? ''} is later than end ${end?.iso ?? ''}.`);
  }
}

function formatYear(year: number): string {
  return year <= 0 ? `${Math.abs(year) || 1} BC` : String(year);
}

export function formatQualifiedTime(time: QualifiedTime): string {
  const parts = parseTimeParts(time.iso, time.precision);
  if (parts === undefined) {
    throw new Error(`Cannot format invalid ${time.precision}-precision time: ${time.iso}.`);
  }
  const year = formatYear(parts[0]);
  const value =
    time.precision === 'day'
      ? `${MONTH_NAMES[parts[1] - 1]} ${parts[2]}, ${year}`
      : time.precision === 'month'
        ? `${MONTH_NAMES[parts[1] - 1]} ${year}`
        : time.precision === 'decade'
          ? `${year}s`
          : year;
  return time.circa === true ? `c. ${value}` : value;
}

function snapshotMonth(snapshotDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(snapshotDate);
  const year = match?.[1] === undefined ? Number.NaN : Number(match[1]);
  const month = match?.[2] === undefined ? Number.NaN : Number(match[2]);
  const day = match?.[3] === undefined ? Number.NaN : Number(match[3]);
  if (
    !Number.isSafeInteger(year) ||
    !Number.isInteger(month) ||
    month < 1 ||
    month > 12 ||
    !Number.isInteger(day) ||
    day < 1 ||
    day > daysInMonth(year, month)
  ) {
    throw new Error(`Invalid relationship data snapshot date: ${snapshotDate}.`);
  }
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

export function formatRelationshipDates(options: {
  start?: QualifiedTime | undefined;
  end?: QualifiedTime | undefined;
  snapshotDate: string;
  includeSnapshotForOpenEnd?: boolean | undefined;
}): string {
  const { start, end } = options;
  assertRelationshipTimeOrder(start, end);
  if (start === undefined && end === undefined) {
    return 'Dates not documented';
  }
  if (start !== undefined && end === undefined) {
    const startLabel = `From ${formatQualifiedTime(start)}`;
    return options.includeSnapshotForOpenEnd === false
      ? startLabel
      : `${startLabel} · data snapshot ${snapshotMonth(options.snapshotDate)}`;
  }
  if (start === undefined && end !== undefined) {
    return `Until ${formatQualifiedTime(end)}`;
  }
  if (start === undefined || end === undefined) {
    throw new Error('Unreachable relationship date boundary.');
  }
  if (start.iso === end.iso && start.precision === end.precision && start.circa === end.circa) {
    return formatQualifiedTime(start);
  }
  if (start.precision === 'month' && end.precision === 'month') {
    const startParts = parseTimeParts(start.iso, start.precision)!;
    const endParts = parseTimeParts(end.iso, end.precision)!;
    if (startParts[0] === endParts[0] && start.circa !== true && end.circa !== true) {
      return `${MONTH_NAMES[startParts[1] - 1]} – ${MONTH_NAMES[endParts[1] - 1]} ${formatYear(endParts[0])}`;
    }
  }
  const compactRange =
    start.precision === end.precision &&
    (start.precision === 'year' || start.precision === 'decade') &&
    start.circa !== true &&
    end.circa !== true;
  const separator = compactRange ? '–' : ' – ';
  return `${formatQualifiedTime(start)}${separator}${formatQualifiedTime(end)}`;
}
